import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Exercise } from "../../../../models/Exercise";
import { TbTicket } from "react-icons/tb";
import { AiOutlineUserAdd } from "react-icons/ai";
import { MdOutlineBackspace, MdClose } from "react-icons/md";
import { IoChevronBack } from "react-icons/io5";
import { useSelector } from "react-redux";
import { State } from "../../../../store";
import useExerciseInfo from "../../exerciseInfo/useExerciseInfo";
import ExerciseInfoPassSelector from "../../exerciseInfo/ExerciseInfoPassSelector";
import EAButton from "../../../shared/Buttons/EAButton";
import EAAlert from "../../../shared/EAAlert";
import { LOGIN_PAGE } from "../../../../routes";
import { useNavigate } from "react-router";
import LightBox from "../../../../../features/LightBox";
import ExerciseInfoBox from "./ExerciseInfoBox";
import ExerciseImageHeader from "./ExerciseImageHeader";
import useDesktopSize from "../../../../../utils/hooks/useDesktopSize";

interface IProps {
  exercise: Exercise;
  handleModals: (value: string) => void;
  joinToExercise: (value: boolean, passId: string) => void;
  leaveFromExercise?: () => void;
  joinErrorData?: { exerciseIds: string; passIds: string } | null;
  alerts: string;
  assignedStatus: boolean;
  ticketsListLength: number;
  showLoginButton?: boolean;
  onClose: () => void;
  onSwitchToTicketPurchase?: () => void;
}

const ExerciseInfoContent: React.FC<IProps> = ({
  handleModals,
  exercise,
  joinToExercise,
  alerts,
  assignedStatus,
  ticketsListLength,
  leaveFromExercise,
  joinErrorData,
  showLoginButton = false,
  onClose,
  onSwitchToTicketPurchase,
}) => {
  const { currentTimev2, loading, availablePasses } = useExerciseInfo();
  const isDesktop = useDesktopSize();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const allowReservationByPassOnFitnessPlan = useSelector(
    (state: State) => state.config.configuration.allowReservationByPassOnFitnessPlan,
  );
  const calendar_AllowSingleTickets = useSelector(
    (state: State) => state.config.configuration.calendar_AllowSingleTickets,
  );
  const blockTimeBeforeExerciseDateChange = useSelector(
    (state: State) => state.config.configuration.blockTimeBeforeExerciseDateChange,
  );
  const [lightBoxOpen, setLightBoxOpen] = useState(false);

  const totalReserved = exercise.reserved + exercise.prereservation;
  const availableSpots = exercise.capacity - totalReserved;
  const hasNoSpaces = availableSpots <= 0;
  const hasWaitlistAvailable = hasNoSpaces && exercise.waitListQuantity > 0;

  // Calculate cancellation info
  const cancellationInfo = blockTimeBeforeExerciseDateChange
    ? { type: "hours" as const, value: Math.floor(blockTimeBeforeExerciseDateChange / 60) }
    : exercise.saleTo
      ? { type: "date" as const, value: exercise.saleTo }
      : null;

  return (
    <>
      {/* Header with title and close button - Desktop only */}
      <div className="single-exercise-details-modal__header">
        <h4>{exercise.name}</h4>
        <MdClose size={28} className="close-button" onClick={onClose} style={{ cursor: "pointer" }} />
      </div>

      {/* Mobile: Image Header with status chips */}
      <div className="exercise-info-mobile-image">
        <ExerciseImageHeader exercise={exercise} />
        <MdClose size={24} className="close-button-mobile" onClick={onClose} style={{ cursor: "pointer" }} />
      </div>

      {/* Info box with details and description */}
      <ExerciseInfoBox
        exercise={exercise}
        onCoachImageClick={() => setLightBoxOpen(true)}
        description={exercise.description}
      />

      <div className="exercise-actions-wrapper">
        {/* Error alerts */}
        {alerts.length > 1 && (
          <EAAlert
            message={alerts}
            type="error"
            buttonText={showLoginButton ? "Zaloguj się" : ""}
            onButtonClick={() => navigate(`${LOGIN_PAGE}?redirect=previousPath`)}
          />
        )}
        {joinErrorData && leaveFromExercise && (
          <EAButton
            title="Wypisz się z tych zajęć"
            startIcon={<MdOutlineBackspace />}
            onClick={leaveFromExercise}
            fullWidth
            color="error"
          />
        )}

        {currentTimev2 > exercise.saleFrom && currentTimev2 < exercise.saleTo && (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "8px" }}>
            {allowReservationByPassOnFitnessPlan && (
              <>
                {assignedStatus ? (
                  <EAButton
                    title={t("zrezygnuj")}
                    onClick={() => joinToExercise(true, "null")}
                    endIcon={<AiOutlineUserAdd />}
                    color="secondary"
                    fullWidth
                  />
                ) : (
                  <>
                    <ExerciseInfoPassSelector
                      exerciseStart={exercise.startTime}
                      reservationList={hasWaitlistAvailable}
                      loading={loading}
                      passesList={availablePasses}
                      joinToExercise={joinToExercise}
                      hasNoSpaces={hasNoSpaces}
                    />
                  </>
                )}
              </>
            )}

            {/* Single ticket button */}
            {calendar_AllowSingleTickets && ticketsListLength > 0 && !hasNoSpaces && (
              <EAButton
                title={assignedStatus ? t("dokup_jednorazowe_wejscie") : t("kup_jednorazowy_bilet")}
                disabled={hasNoSpaces}
                onClick={() => (onSwitchToTicketPurchase ? onSwitchToTicketPurchase() : handleModals("ticketsModal"))}
                endIcon={<TbTicket />}
                fullWidth
                variant={"outlined"}
              />
            )}
          </div>
        )}

        {/* Cancellation info */}
        {cancellationInfo && currentTimev2 < exercise.startTime && (
          <div className="exercise-cancellation-info">
            {cancellationInfo.type === "hours"
              ? t("odwolanie_mozliwe_do", { hours: cancellationInfo.value })
              : t("odwolanie_mozliwe_do_daty", {
                  date: format(cancellationInfo.value, "dd.MM.yyyy, HH:mm"),
                })}
          </div>
        )}

        {/* Back button - Mobile only */}
        {!isDesktop && (
          <EAButton title="Powrót" startIcon={<IoChevronBack />} onClick={onClose} fullWidth variant="text" />
        )}
      </div>

      {/* LightBox for coach image */}
      {lightBoxOpen && exercise.humanPicture && (
        <LightBox
          imageUrl={exercise.humanPicture}
          open={lightBoxOpen}
          closeModal={() => setLightBoxOpen(false)}
          onAccept={() => {}}
        />
      )}
    </>
  );
};

export default ExerciseInfoContent;
